import React from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import { Repeat } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import { reservationOwnSx, reservationOtherSx, focusVisibleSx } from './stiklainisStyles';
import { Reservation } from './types';
import { formatReservationTime } from './formatTime';

interface ReservationBlockProps {
  reservation: Reservation;
  isOwn: boolean;
  top: number;
  height: number;
  onClick: (e: React.MouseEvent | React.KeyboardEvent) => void;
  variant?: 'week' | 'month';
}

const titleClampSx = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
  wordBreak: 'break-word' as const,
};

export function isSeriesReservation(reservation: Reservation): boolean {
  return Boolean(reservation.isRecurring || reservation.parentReservationId);
}

const ReservationBlock: React.FC<ReservationBlockProps> = ({
  reservation,
  isOwn,
  top,
  height,
  onClick,
  variant = 'week',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = isOwn ? theme.palette.success.main : theme.palette.primary.main;
  const accentDark = isOwn ? theme.palette.success.dark : theme.palette.primary.dark;
  const colorSx = isOwn ? reservationOwnSx(theme) : reservationOtherSx(theme);
  const timeRange = `${formatReservationTime(reservation.startTime)} – ${formatReservationTime(reservation.endTime)}`;
  const organizer = `${reservation.user.firstName} ${reservation.user.lastName}`;
  const recurring = isSeriesReservation(reservation);
  const ariaLabel = [
    reservation.title,
    timeRange,
    organizer,
    recurring ? t('stiklainis.recurring') : '',
  ]
    .filter(Boolean)
    .join(', ');

  const showOrganizer = variant === 'month' || height >= 44;
  const showTime = variant === 'month' || height >= 28;
  const titleLines = variant === 'month' ? 1 : Math.max(1, Math.floor((height - (showTime ? 14 : 0) - 8) / 14));

  const activate = (e: React.MouseEvent | React.KeyboardEvent) => {
    onClick(e);
  };

  // Opaque week cards so hour grid lines do not show through longer meetings
  const weekSurfaceSx = {
    color: accentDark,
    borderLeft: `3px solid ${accent}`,
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `linear-gradient(${alpha(accent, 0.2)}, ${alpha(accent, 0.2)})`,
    boxShadow: theme.shadows[2],
  };

  const block = (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          activate(e);
        }
      }}
      sx={{
        ...(variant === 'week' ? weekSurfaceSx : colorSx),
        borderRadius: variant === 'month' ? 0.5 : 1,
        px: 0.75,
        py: variant === 'month' ? 0.35 : 0.5,
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.15,
        isolation: 'isolate',
        ...focusVisibleSx,
        ...(variant === 'month'
          ? { mt: 0.4, boxShadow: 0 }
          : {
              position: 'absolute',
              left: 4,
              right: 4,
              top,
              height,
              minHeight: 22,
              zIndex: 4,
            }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, minWidth: 0 }}>
        {recurring && (
          <Repeat
            sx={{ fontSize: 12, flexShrink: 0, opacity: 0.85 }}
            aria-hidden
          />
        )}
        {showTime && (
          <Typography
            variant="caption"
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: variant === 'month' ? '0.65rem' : '0.68rem',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {formatReservationTime(reservation.startTime)}
          </Typography>
        )}
        <Typography
          variant="caption"
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: variant === 'month' ? '0.68rem' : '0.7rem',
            lineHeight: 1.25,
            minWidth: 0,
            m: 0,
            ...titleClampSx,
            WebkitLineClamp: titleLines,
          }}
        >
          {reservation.title}
        </Typography>
      </Box>
      {showOrganizer && (
        <Typography
          variant="caption"
          component="span"
          sx={{
            fontSize: '0.62rem',
            lineHeight: 1.15,
            opacity: 0.85,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {organizer}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={
        <Box sx={{ py: 0.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {reservation.title}
            {recurring ? ` · ${t('stiklainis.recurring')}` : ''}
          </Typography>
          <Typography variant="caption" display="block">
            {timeRange}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
            {t('stiklainis.organizer')}: {organizer}
          </Typography>
        </Box>
      }
      enterDelay={300}
      slotProps={{ tooltip: { sx: { maxWidth: 280 } } }}
    >
      {block}
    </Tooltip>
  );
};

export default ReservationBlock;
